import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';

import { SyncMeetingsSteps } from '../types';

export function getModalTitle(currentStep) {
  switch (currentStep) {
    case SyncMeetingsSteps.newOneOnOneMeetings:
      return 'New 1:1 Meetings from your Calendar';
    case SyncMeetingsSteps.createNewTeammateWorkspaces:
      return 'Create 1:1 Workspaces from your Calendar Events';
    case SyncMeetingsSteps.createNewGroupWorkspaces:
      return 'Create Group Workspaces from your Calendar Events';
    case SyncMeetingsSteps.inviteTeammates:
      return 'Invite your teammates';
    case SyncMeetingsSteps.linkExistingMeetings:
    default:
      return 'Link Existing Meetings with Workspaces';
  }
}

export function getRecurringMeetingsForTeammmatesWorkspaces({
  recurringMeetings,
  currentUserId,
  teammatesIds,
  highConfidence,
}) {
  return recurringMeetings
    .filter(({ attendees }) => {
      if (highConfidence) {
        return attendees.find(({ userId }) => userId === currentUserId) && attendees.length === 2;
      } else {
        return true;
      }
    })
    .filter(({ attendees }) => {
      if (
        (attendees.find(({ userId }) => userId === currentUserId) && attendees.length <= 2) ||
        attendees.length <= 1
      ) {
        const teammate = attendees.find(({ userId }) => userId !== currentUserId);
        if (teammatesIds && teammate && teammatesIds.find(teammateId => teammateId === teammate.userId)) return false;
        return true;
      }
      return false;
    });
}

export function getRecurringMeetingsForGroupWorkspaces({ recurringMeetings }) {
  return recurringMeetings.filter(({ attendees }) => attendees.length > 2);
}

function getFormattedRecurringMeeting(recurringMeeting) {
  return {
    ...recurringMeeting,
    label: recurringMeeting.name,
    value: recurringMeeting.id,
    selected: true,
  };
}

export function getInitialWorkspaces({ workspaces, linkedWorkspaces, recurringMeetings }) {
  let initialWorkspaces = [];
  if (!linkedWorkspaces)
    initialWorkspaces = workspaces.map(workspace => {
      const suggestedRecurringMeeting = recurringMeetings?.find(
        ({ suggestedWorkspaceId }) => suggestedWorkspaceId === workspace.id
      );
      if (suggestedRecurringMeeting)
        return {
          ...workspace,
          recurringMeeting: getFormattedRecurringMeeting(suggestedRecurringMeeting),
        };
      return workspace;
    });
  else
    initialWorkspaces = workspaces.map(workspace => {
      const linkedWorkspace = linkedWorkspaces?.find(({ id }) => id === workspace.id);
      if (linkedWorkspace) return linkedWorkspace;
      return workspace;
    });

  return sortBy(initialWorkspaces, 'recurringMeeting');
}

export function generateWorkspacesParams({ recurringMeetings, currentUserId, isGroup }) {
  return recurringMeetings.map(({ attendees, id, name }) => {
    const teammate = attendees.find(({ userId }) => currentUserId !== userId);
    return {
      name: isGroup ? name : teammate?.fullName || teammate?.email,
      isGroup,
      recurringMeetingId: id,
      inviteeUsers: attendees
        .filter(({ userId }) => currentUserId !== userId)
        .map(({ email, userId }) => ({ email, userId })),
    };
  });
}

export function generateLinkedWorkspacesParams({ selectedWorkspaces, currentUserId }) {
  return (selectedWorkspaces || []).map(workspace => {
    const { users, recurringMeeting, isGroup } = workspace;
    let inviteeUsers = [];
    if (isGroup) {
      inviteeUsers = recurringMeeting.attendees?.filter(
        ({ email, userId }) =>
          userId !== currentUserId && !users.find(({ id, email: userEmail }) => id === userId || email === userEmail)
      );
    }
    if (
      !isGroup &&
      users.length === 1 &&
      recurringMeeting.attendees?.length === 2 &&
      recurringMeeting.attendees?.find(({ userId }) => userId === currentUserId)
    ) {
      inviteeUsers = [recurringMeeting.attendees?.find(({ userId }) => userId !== currentUserId)];
    }
    return { ...workspace, inviteeUsers };
  });
}

export function getInitialInviteeEmails({
  selectedTeammateWorkspaces,
  selectedGroupWorkspaces,
  currentUserEmail,
  linkedWorkspaces,
  memberIds,
}) {
  const inviteeUsersForWorkspaces = flatten(
    [...(selectedTeammateWorkspaces || []), ...(selectedGroupWorkspaces || []), ...(linkedWorkspaces || [])].map(
      ({ inviteeUsers }) => inviteeUsers
    )
  );

  return sortBy(
    uniqBy(
      inviteeUsersForWorkspaces.filter(({ email }) => email !== currentUserEmail),
      'email'
    ).map(({ email, userId }) => {
      const alreadyOnWP = Boolean(userId) && memberIds.includes(userId);
      return {
        email,
        alreadyOnWP,
        selected: alreadyOnWP,
      };
    }),
    ['alreadyOnWP']
  );
}

export function getRecurringMeetingsForSyncMeetingStep({
  recurringMeetings,
  currentStep,
  teammatesIds,
  currentUserId,
  linkedWorkspaces,
  selectedTeammateWorkspaces,
  selectedGroupWorkspaces,
  customFilterForOneOnOneSuggestions,
  customFilterForGroupSuggestions,
}) {
  const selectedForLinkRecurringMeetings = linkedWorkspaces?.map(({ recurringMeeting }) => recurringMeeting.id) || [];
  switch (currentStep) {
    case SyncMeetingsSteps.linkExistingMeetings:
      return recurringMeetings.map(recurringMeeting => getFormattedRecurringMeeting(recurringMeeting));
    case SyncMeetingsSteps.newOneOnOneMeetings:
    case SyncMeetingsSteps.createNewTeammateWorkspaces:
      return (
        customFilterForOneOnOneSuggestions
          ? customFilterForOneOnOneSuggestions({ recurringMeetings })
          : getRecurringMeetingsForTeammmatesWorkspaces({
              recurringMeetings,
              teammatesIds,
              currentUserId,
              highConfidence: true,
            })
      )
        .filter(({ id }) => !selectedForLinkRecurringMeetings.includes(id))
        .map(recurringMeeting => ({
          ...recurringMeeting,
          selected: selectedTeammateWorkspaces
            ? selectedTeammateWorkspaces.some(({ recurringMeetingId }) => recurringMeetingId === recurringMeeting.id)
            : true,
        }));
    case SyncMeetingsSteps.createNewGroupWorkspaces:
      return (
        customFilterForGroupSuggestions
          ? customFilterForGroupSuggestions({ recurringMeetings })
          : getRecurringMeetingsForGroupWorkspaces({ recurringMeetings })
      )
        .filter(({ id }) => !selectedForLinkRecurringMeetings.includes(id))
        .map(recurringMeeting => ({
          ...recurringMeeting,
          selected: selectedGroupWorkspaces
            ? selectedGroupWorkspaces.some(({ recurringMeetingId }) => recurringMeetingId === recurringMeeting.id)
            : true,
        }));
    default:
      return [];
  }
}

export function selectedRecurringMeetingsIdsDecorator(newWorkspaces) {
  return newWorkspaces
    .filter(({ recurringMeeting }) => recurringMeeting)
    .map(({ recurringMeeting }) => recurringMeeting?.id);
}
